/**
 * DOB helpers: treat API values as calendar dates (strip `T00:00:00.000Z`, no timezone shift).
 */

import { EngageReferral } from "../services/engage-activity.service";
import { expandPatientDirectoryListRow } from "./phi-patient-directory-list.util";

/** Local calendar day key (`yyyy-MM-dd`) for DOB filters — no timezone shift on `T00:00:00.000Z`. */
export function dobCalendarDayKey(
  value: string | null | undefined
): string | null {
  const iso = isoDateOnlyFromDob(value);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
}

/** Returns `YYYY-MM-DD` from ISO datetime or date-only strings. */
export function isoDateOnlyFromDob(value: string | null | undefined): string {
  const s = String(value ?? "").trim();
  if (!s) {
    return "";
  }
  const tIdx = s.indexOf("T");
  if (tIdx > 0) {
    const datePart = s.slice(0, tIdx);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  return s;
}

/** `m/d/yyyy` when padded is false; `MM/dd/yyyy` when padded is true. */
export function formatDobMdY(
  value: string | null | undefined,
  padded = false
): string {
  const s = String(value ?? "").trim();
  if (!s) {
    return "";
  }

  const isoOnly = isoDateOnlyFromDob(s);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoOnly);
  if (iso) {
    const y = +iso[1];
    const mo = +iso[2];
    const d = +iso[3];
    if (padded) {
      return `${String(mo).padStart(2, "0")}/${String(d).padStart(2, "0")}/${y}`;
    }
    return `${mo}/${d}/${y}`;
  }

  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (slash) {
    const mo = +slash[1];
    const d = +slash[2];
    const y = +slash[3];
    if (padded) {
      return `${String(mo).padStart(2, "0")}/${String(d).padStart(2, "0")}/${y}`;
    }
    return `${mo}/${d}/${y}`;
  }

  return "";
}

/** Rewrites `DOB:` lines in activity note bodies (e.g. ISO timestamps → `MM/dd/yyyy`). */
export function formatActivityNoteDobLines(text: string | null | undefined): string {
  const raw = String(text ?? "");
  if (!raw) {
    return raw;
  }
  return raw.replace(
    /(^|\n)(DOB:\s*)([^\n]*)/g,
    (_match, prefix: string, label: string, dobPart: string) => {
      const trimmed = String(dobPart ?? "").trim();
      if (!trimmed || trimmed === "—") {
        return `${prefix}${label}${dobPart}`;
      }
      const formatted = formatDobMdY(trimmed, true);
      return formatted
        ? `${prefix}${label}${formatted}`
        : `${prefix}${label}${dobPart}`;
    }
  );
}

/** Local calendar `Date` for age math (no UTC midnight shift). */
export function dobToLocalDate(value: string | null | undefined): Date | null {
  const isoOnly = isoDateOnlyFromDob(value);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoOnly);
  if (iso) {
    const y = +iso[1];
    const mo = +iso[2];
    const d = +iso[3];
    if (y && mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return new Date(y, mo - 1, d);
    }
  }

  const s = String(value ?? "").trim();
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (slash) {
    const mo = +slash[1];
    const d = +slash[2];
    const y = +slash[3];
    if (y && mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return new Date(y, mo - 1, d);
    }
  }

  return null;
}

/** True when Angular DatePipe format uses zero-padded month/day (e.g. `MM/dd/yyyy`). */
export function dobDisplayFormatUsesPadding(format: string | undefined): boolean {
  return !!(format && (format.includes("MM") || format.includes("dd")));
}

/** True when the value carries a `YYYY-MM-DD` calendar date (ISO date or `…T00:00:00.000Z`). */
export function hasIsoCalendarDob(value: string | null | undefined): boolean {
  const iso = isoDateOnlyFromDob(String(value ?? "").trim());
  return /^\d{4}-\d{2}-\d{2}$/.test(iso);
}

/**
 * Prefer ISO calendar DOB (Engage referral / API) over legacy `MM/DD/YYYY` strings that may
 * have been shifted by timezone when saved to PHI.
 */
export function pickEngageDisplayDob(
  ...candidates: Array<string | null | undefined>
): string {
  let isoSource = "";
  let fallback = "";
  for (const raw of candidates) {
    const s = String(raw ?? "").trim();
    if (!s) {
      continue;
    }
    if (hasIsoCalendarDob(s)) {
      if (!isoSource) {
        isoSource = s;
      }
    } else if (!fallback) {
      fallback = s;
    }
  }
  return isoSource || fallback;
}

/** Read DOB from an Engage referral row (flat or nested patient). */
export function extractEngageReferralDob(
  ref: Record<string, unknown> | null | undefined
): string {
  if (!ref || typeof ref !== "object") {
    return "";
  }
  const expanded = expandPatientDirectoryListRow(
    ref as EngageReferral
  ) as Record<string, unknown>;
  const pick = (...keys: string[]): string => {
    for (const key of keys) {
      const raw = expanded[key];
      if (raw != null && String(raw).trim() !== "") {
        return String(raw).trim();
      }
    }
    return "";
  };
  const direct = pick(
    "dob",
    "dateOfBirth",
    "date_of_birth",
    "patientDob",
    "patient_dob"
  );
  if (direct) {
    return direct;
  }
  const nests: unknown[] = [
    expanded["patient"],
    expanded["Patient"],
    (expanded["referral"] as Record<string, unknown> | undefined)?.["patient"],
  ];
  for (const node of nests) {
    if (node == null || typeof node !== "object" || Array.isArray(node)) {
      continue;
    }
    const o = node as Record<string, unknown>;
    for (const key of ["dob", "dateOfBirth", "date_of_birth"]) {
      const raw = o[key];
      if (raw != null && String(raw).trim() !== "") {
        return String(raw).trim();
      }
    }
  }
  return "";
}
